/**
 * CuttySark 賞の候補者写真をアップロード前にブラウザで縮める。
 *
 * スマホで撮った写真は数 MB あるので、そのまま送ると API の上限（400KB）を超える。
 * 長辺 600px の JPEG に縮めると 1 枚 100KB 前後になり、紹介アニメーションで大きく出しても
 * 粗さが目立たない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞の候補者写真をアップロード前に長辺 600px の JPEG に縮める
 * @graph-connects none
 */

/** 縮めた後の長辺（px）。 @graph-connects none */
export const PHOTO_MAX_EDGE = 600;
/** JPEG の画質。 @graph-connects none */
const JPEG_QUALITY = 0.85;

/**
 * 縦横比を保ったまま長辺を maxEdge 以下に収めたサイズ。小さい画像は拡大しない。
 * @graph-connects none
 */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number = PHOTO_MAX_EDGE,
): { width: number; height: number } {
  const scale = Math.min(1, maxEdge / Math.max(width, height, 1));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * data URL から base64 本体だけを取り出す。
 * @graph-connects none
 */
export function base64FromDataUrl(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma === -1 ? '' : dataUrl.slice(comma + 1);
}

/**
 * 画像ファイルを長辺 600px の JPEG（base64）に変換する。
 * @graph-connects fitWithin [calls] 縮めた後のサイズ
 * @graph-connects base64FromDataUrl [calls] base64 本体の取り出し
 */
export async function resizePhotoToJpeg(
  file: File,
): Promise<{ contentType: 'image/jpeg'; data: string }> {
  const bitmap = await createImageBitmap(file);
  const { width, height } = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('画像を縮められませんでした');
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  return {
    contentType: 'image/jpeg',
    data: base64FromDataUrl(canvas.toDataURL('image/jpeg', JPEG_QUALITY)),
  };
}
